import { type UseMutationResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import { MeetingNoteVisibility, type TranscriptBannerKey } from "@performyard/models";
import { toast } from "@performyard/py-components";
import type {
	ActionItemSearchServiceFrontendResponse,
	CreateActionItemRequestJson,
	CreateAgendaItemRequestJson,
	CreateMeetingRequestJson,
	CreateMeetingWorkspaceRequestJson,
	CreateRecurringSeriesRequestJson,
	GetMeetingDetailsResponseJson,
	GetMeetingWorkspacesSearchServiceFrontendResponse,
	GetMeetingsSearchServiceFrontendResponse,
	GetUpcomingMeetingsResponseFrontend,
	MeetingExtendedModelJson,
	MeetingModelJson,
	RetrieveMeetingTranscriptResponseJson,
	UpdateActionItemRequestJson,
	UpdateAgendaItemRequestJson,
	UpdateMeetingNoteRequestJson,
	UpdateMeetingRequestJson,
	UpdateMeetingWorkspaceRequestJson,
	UpdateRecurringSeriesRequestJson,
} from "@performyard/service-contracts";

import { getCalendarSuccessToastMessage, getErrorToastMessage } from "../../../pages/Meetings/MeetingsTable/utils";

import {
	actionItemsSearchQuery,
	latestMeetingCarryOverItemsQuery,
	meetingActionItemsQuery,
	meetingAgendaItemsQuery,
	meetingDetailsQuery,
	meetingNotesQuery,
	meetingTranscriptQuery,
	meetingWorkspaceDetailsQuery,
	meetingWorkspacesSearchQuery,
	meetingsSearchQuery,
	recurringSeriesByWorkspaceQuery,
	upcomingMeetingsQuery,
	userCalendarIntegrationStatusQuery,
} from "../../../api/queries";
import {
	type GetActionItemsSearchParams,
	type GetMeetingWorkspacesParams,
	type GetMeetingsSearchParams,
	type GetUpcomingMeetingsParams,
	addMeetingActionItem,
	addMeetingAgendaItem,
	addWorkspaceMeeting,
	addWorkspaceRecurringSeries,
	deleteWorkspaceRecurringSeries,
	dismissTranscriptBanner,
	editMeetingActionItem,
	editMeetingAgendaItem,
	editWorkspaceMeeting,
	editWorkspaceRecurringSeries,
	patchMeetingWorkspace,
	postMeetingWorkspace,
	reorderMeetingActionItems,
	reorderMeetingAgendaItems,
	retrieveMeetingTranscript,
	updateMeetingTranscript,
	updatePrivateMeetingNotes,
	updatePublicMeetingNotes,
} from "../../../api/services/defs/internal-api";

import { formatErrorMessage } from "../../../utils/TanstackQueryErrorHandling";

import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

const isMeetingWorkspaceInsightQuery = (queryKey: readonly unknown[], workspaceId: string): boolean => {
	if (queryKey[0] !== "ai-query") return false;
	if (queryKey[1] !== "meeting-workspace-insight" && queryKey[1] !== "team-meeting-workspace-insight") return false;
	if (typeof queryKey[2] !== "string") return false;

	try {
		const requestProps = JSON.parse(queryKey[2]) as { workspaceId?: unknown };
		return requestProps.workspaceId === workspaceId;
	} catch {
		return false;
	}
};

export const useCreateMeetingWorkspace = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { onSuccess: originalOnSuccess, ...restOptions } = options || {};
	return useMutation({
		mutationFn: async (data: CreateMeetingWorkspaceRequestJson) => postMeetingWorkspace(data),
		...restOptions,
		onSuccess: (response, variables, context, mutation) => {
			client.invalidateQueries({ queryKey: ["get-meeting-workspaces-search"] });
			originalOnSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useUpdateMeetingWorkspace = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { onSuccess: originalOnSuccess, ...restOptions } = options || {};
	return useMutation({
		mutationFn: async (props: { workspaceId: string; data: UpdateMeetingWorkspaceRequestJson }) =>
			patchMeetingWorkspace(props.workspaceId, props.data),
		...restOptions,
		onSuccess: (response, variables, context, mutation) => {
			// Participant cascade can promote the workspace and clear server-side 1:1
			// insight logs, so the insight cache must be busted alongside meeting caches.
			client.invalidateQueries({
				predicate: (query) => {
					const shouldInvalidateWorkspaceData = [
						"get-meeting-workspaces-search",
						"get-upcoming-meetings",
						"get-meeting-workspace-by-id",
						"get-meeting-details",
						"get-meeting-action-items-by-meeting",
						"get-meeting-agenda-items-by-meeting",
						"get-meetings-search",
					].includes(query.queryKey[0] as string);
					return (
						shouldInvalidateWorkspaceData ||
						isMeetingWorkspaceInsightQuery(query.queryKey, variables.workspaceId)
					);
				},
				exact: false,
			});
			originalOnSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useMeetingWorkspacesSearch = (
	params: GetMeetingWorkspacesParams,
	options?: PYUseQueryOptions<GetMeetingWorkspacesSearchServiceFrontendResponse>
) => {
	const query = useQuery<GetMeetingWorkspacesSearchServiceFrontendResponse>({
		...meetingWorkspacesSearchQuery(params),
		...options,
	});

	return {
		data: query?.data?.results || [],
		totalCount: query?.data?.total || 0,
		isLoading: query?.isLoading,
	};
};

export const useMeetingWorkspaceDetails = (workspaceId: string) => {
	const query = useQuery({
		...meetingWorkspaceDetailsQuery(workspaceId),
		enabled: !!workspaceId, // Only run query when workspaceId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingDetails = (meetingId: string) => {
	const query = useQuery({
		...meetingDetailsQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data,
		meeting: query?.data?.meeting,
		workspace: query?.data?.workspace,
		isLoading: query?.isLoading,
		error: query?.error,
	};
};

export const useMeetingAgendaItems = (meetingId: string) => {
	const query = useQuery({
		...meetingAgendaItemsQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingActionItems = (meetingId: string) => {
	const query = useQuery({
		...meetingActionItemsQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingNotes = (meetingId: string, visibility?: MeetingNoteVisibility) => {
	const query = useQuery({
		...meetingNotesQuery(meetingId, visibility),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingTranscript = (meetingId: string) => {
	const query = useQuery({
		...meetingTranscriptQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useAddWorkspaceMeeting = (
	handleCloseModal?: () => void,
	setExpandedMeetingIds?: React.Dispatch<React.SetStateAction<string[]>>,
	onCalendarError?: (error: unknown, variables: { workspaceId: string; data: CreateMeetingRequestJson }) => boolean
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ workspaceId, data }: { workspaceId: string; data: CreateMeetingRequestJson }) =>
			await addWorkspaceMeeting(workspaceId, data),
		onSuccess: async (data, variables) => {
			// Creating a 3+ meeting can trigger server-side workspace promotion and 1:1
			// insight-log cleanup — refetch the workspace doc and bust affected insight caches.
			await queryClient.invalidateQueries({
				predicate: (query) => {
					const shouldInvalidateWorkspaceData = [
						"get-meetings-search",
						"get-meeting-agenda-items-by-meeting",
						"get-meeting-action-items-by-meeting",
						"get-meeting-workspaces-search",
						"get-upcoming-meetings",
						"get-meeting-workspace-by-id",
					].includes(query.queryKey[0] as string);
					return (
						shouldInvalidateWorkspaceData ||
						isMeetingWorkspaceInsightQuery(query.queryKey, variables.workspaceId)
					);
				},
				exact: false,
			});
			setExpandedMeetingIds?.((prev) => [...prev, data._id]);

			// Use centralized success message logic for calendar operations
			if (variables.data.addToCalendar && data.calendarEvent?.eventId) {
				const successMessage = getCalendarSuccessToastMessage({
					calendarOperationType: "created",
					operationData: variables.data,
					isInitialCreation: true,
				});
				toast.success(successMessage);
			} else {
				toast.success("Meeting Created");
			}

			handleCloseModal?.();
		},
		onError: (error, variables) => {
			// A failed calendar op may have cleared a dead integration server-side; resync the
			// status so the form's "add to calendar" checkbox reflects reality.
			if (variables.data.addToCalendar) {
				queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
			}
			// Always try calendar error handling first - let the handler decide if it's a calendar error
			if (onCalendarError) {
				const wasHandled = onCalendarError(error, variables);
				if (wasHandled) {
					return;
				}
			}
			// Otherwise surface the backend message if present, else a generic fallback
			formatErrorMessage(error, { meta: { errorMessage: "Failed to Create Meeting" } }).forEach((message) =>
				toast.error(message)
			);
		},
		meta: {
			disableDefaultErrorHandling: true,
		},
	});
};

export const useEditWorkspaceMeeting = (
	handleCloseModal?: () => void,
	onCalendarError?: (error: unknown, variables: { meetingId: string; data: UpdateMeetingRequestJson }) => boolean
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ meetingId, data }: { meetingId: string; data: UpdateMeetingRequestJson }) =>
			await editWorkspaceMeeting(meetingId, data),
		onSuccess: (data, variables) => {
			// Completing or rescheduling a series meeting can materialize the next
			// occurrence server-side — refresh the series section too.
			queryClient.invalidateQueries({
				predicate: (query) => {
					return [
						"get-meetings-search",
						"get-meeting-workspaces-search",
						"get-meeting-details",
						"get-recurring-series-by-workspace",
					].includes(query.queryKey[0] as string);
				},
				exact: false,
			});

			// Use centralized success message logic
			const successMessage = getCalendarSuccessToastMessage({
				calendarOperationType: data.calendarOperationType,
				operationData: variables.data,
				isInitialCreation: false,
			});

			// Handle calendar event not found with specific warning
			if (data.calendarOperationType === "calendar_event_not_found") {
				toast.error("Meeting Updated Successfully. Connected Calendar Event Not Found.");
				handleCloseModal?.();
				return; // Early return to avoid the standard success toast
			}

			// Company disabled video conferencing — the PY meeting updated but the upstream meeting was skipped
			if (data.videoConferenceOperationType === "video_conference_not_found") {
				toast.error("Meeting Updated Successfully. Connected Video Conferencing Not Found.");
				handleCloseModal?.();
				return; // Early return to avoid the standard success toast
			}

			if (successMessage) {
				toast.success(successMessage);
			}
			handleCloseModal?.();
		},
		onError: (error, variables) => {
			// A failed calendar op may have cleared a dead integration server-side; resync the
			// status so the form's "add to calendar" checkbox reflects reality.
			if (variables.data.addToCalendar || variables.data.removeFromCalendar) {
				queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
			}
			// Always try calendar error handling first - let the handler decide if it's a calendar error
			if (onCalendarError) {
				const wasHandled = onCalendarError(error, variables);
				// If the calendar error handler didn't handle it, fall through to generic error handling
				if (wasHandled) {
					return;
				}
			}

			// Surface the backend message if present, else the centralized operation-specific fallback
			formatErrorMessage(error, { meta: { errorMessage: getErrorToastMessage(variables.data) } }).forEach(
				(message) => toast.error(message)
			);
		},
		meta: {
			disableDefaultErrorHandling: true,
		},
	});
};

export const useWorkspaceRecurringSeries = (workspaceId: string, options: { includeDeleted?: boolean } = {}) => {
	const query = useQuery({
		...recurringSeriesByWorkspaceQuery(workspaceId, options.includeDeleted),
		enabled: !!workspaceId,
	});

	return {
		data: query?.data ?? [],
		isLoading: query?.isLoading,
		isSuccess: query?.isSuccess,
	};
};

const invalidateRecurringSeriesQueries = (queryClient: ReturnType<typeof useQueryClient>): Promise<void> =>
	queryClient.invalidateQueries({
		predicate: (query) =>
			[
				"get-recurring-series-by-workspace",
				"get-meetings-search",
				"get-meeting-workspaces-search",
				"get-upcoming-meetings",
				"get-meeting-workspace-by-id",
				// Editing can move the upcoming meeting; ending preserves existing meetings
				"get-meeting-details",
			].includes(query.queryKey[0] as string),
		exact: false,
	});

export const useAddWorkspaceRecurringSeries = (
	handleCloseModal?: () => void,
	onVideoConferencingError?: (
		error: unknown,
		variables: { workspaceId: string; data: CreateRecurringSeriesRequestJson }
	) => boolean
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ workspaceId, data }: { workspaceId: string; data: CreateRecurringSeriesRequestJson }) =>
			await addWorkspaceRecurringSeries(workspaceId, data),
		onSuccess: async () => {
			await invalidateRecurringSeriesQueries(queryClient);
			toast.success("Recurring Meeting Created");
			handleCloseModal?.();
		},
		onError: (error, variables) => {
			// Series creation fails whole on a provider error (no partial series), so the
			// handler can offer retry / continue-without exactly like meeting creation.
			if (onVideoConferencingError?.(error, variables)) {
				return;
			}
			formatErrorMessage(error, { meta: { errorMessage: "Failed to Create Recurring Meeting" } }).forEach(
				(message) => toast.error(message)
			);
		},
		meta: {
			disableDefaultErrorHandling: true,
		},
	});
};

export const useEditWorkspaceRecurringSeries = (handleCloseModal?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			workspaceId,
			seriesId,
			data,
		}: {
			workspaceId: string;
			seriesId: string;
			data: UpdateRecurringSeriesRequestJson;
		}) => await editWorkspaceRecurringSeries(workspaceId, seriesId, data),
		onSuccess: async () => {
			await invalidateRecurringSeriesQueries(queryClient);
			toast.success("Recurring Meeting Updated");
			handleCloseModal?.();
		},
		meta: {
			errorMessage: "Failed to Update Recurring Meeting",
		},
	});
};

export const useDeleteWorkspaceRecurringSeries = (handleCloseModal?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			workspaceId,
			seriesId,
			removeCalendarEvents,
		}: {
			workspaceId: string;
			seriesId: string;
			removeCalendarEvents?: boolean;
		}) => await deleteWorkspaceRecurringSeries(workspaceId, seriesId, removeCalendarEvents),
		onSuccess: async () => {
			await invalidateRecurringSeriesQueries(queryClient);
			toast.success("Recurring Meeting Ended");
			handleCloseModal?.();
		},
		meta: {
			errorMessage: "Failed to End Recurring Meeting",
		},
	});
};

export const useMeetingsSearch = <T extends "companyMeetings" | "workspaceDetails" = "workspaceDetails">(
	params: GetMeetingsSearchParams,
	options?: PYUseQueryOptions<GetMeetingsSearchServiceFrontendResponse>
) => {
	const query = useQuery<GetMeetingsSearchServiceFrontendResponse>({
		...meetingsSearchQuery(params),
		...options,
	});

	// Type assertion based on the context of use
	// When the generic type is 'companyMeetings', we expect MeetingExtendedModelJson
	// Otherwise we get regular MeetingModelJson
	type ResultType = T extends "companyMeetings" ? MeetingExtendedModelJson[] : MeetingModelJson[];

	return {
		data: (query?.data?.results as ResultType) || [],
		totalCount: query?.data?.total || 0,
		isLoading: query?.isLoading,
		isFetching: query?.isFetching,
	};
};

export const useLatestMeetingCarryOverItems = (workspaceId: string, startDateTime: Date, enabled: boolean) => {
	const query = useQuery({
		enabled,
		...latestMeetingCarryOverItemsQuery(workspaceId, startDateTime),
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useActionItemsSearch = (
	params: GetActionItemsSearchParams,
	options?: PYUseQueryOptions<ActionItemSearchServiceFrontendResponse>
) => {
	const query = useQuery<ActionItemSearchServiceFrontendResponse>({
		...actionItemsSearchQuery(params),
		...options,
	});

	return {
		data: query?.data?.results || [],
		totalCount: query?.data?.total || 0,
		isLoading: query?.isLoading,
	};
};

export const useAddMeetingAgendaItem = () => {
	return useMutation({
		mutationFn: async ({ data }: { data: CreateAgendaItemRequestJson }) => await addMeetingAgendaItem(data),
	});
};

export const useEditMeetingAgendaItem = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ agendaItemId, data }: { agendaItemId: string; data: UpdateAgendaItemRequestJson }) =>
			await editMeetingAgendaItem(agendaItemId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-agenda-items-by-meeting"] });
		},
	});
};

export const useReorderMeetingAgendaItems = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ meetingId, agendaItemIds }: { meetingId: string; agendaItemIds: string[] }) =>
			await reorderMeetingAgendaItems(meetingId, agendaItemIds),
		// Serialize consecutive reorders so a slower earlier request can't
		// overwrite the final intended order server-side.
		scope: { id: "reorder-meeting-agenda-items" },
		onSettled: (_data, _error, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-agenda-items-by-meeting", meetingId] });
		},
	});
};

export const useAddMeetingActionItem = () => {
	return useMutation({
		mutationFn: async ({ data }: { data: CreateActionItemRequestJson }) => await addMeetingActionItem(data),
	});
};

export const useEditMeetingActionItem = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ actionItemId, data }: { actionItemId: string; data: UpdateActionItemRequestJson }) =>
			await editMeetingActionItem(actionItemId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-action-items-by-meeting"] });
			queryClient.invalidateQueries({ queryKey: ["get-action-items-search"] });
		},
	});
};

export const useReorderMeetingActionItems = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ meetingId, actionItemIds }: { meetingId: string; actionItemIds: string[] }) =>
			await reorderMeetingActionItems(meetingId, actionItemIds),
		// Serialize consecutive reorders so a slower earlier request can't
		// overwrite the final intended order server-side.
		scope: { id: "reorder-meeting-action-items" },
		onSettled: (_data, _error, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-action-items-by-meeting", meetingId] });
		},
	});
};

export const useUpdatePublicMeetingNotes = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			noteId,
			data,
		}: {
			noteId: string;
			data: UpdateMeetingNoteRequestJson;
			meetingId: string;
		}) => await updatePublicMeetingNotes(noteId, data),
		onSuccess: (_, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-notes-by-meeting", meetingId] });
			// Mark meetings search cache as stale without refetching immediately (needed for filtering)
			queryClient.invalidateQueries({
				queryKey: ["get-meetings-search"],
				refetchType: "none",
			});
		},
	});
};

export const useUpdatePrivateMeetingNotes = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			noteId,
			data,
		}: {
			noteId: string;
			data: UpdateMeetingNoteRequestJson;
			meetingId: string;
		}) => await updatePrivateMeetingNotes(noteId, data),
		onSuccess: (_, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-notes-by-meeting", meetingId] });
			// Mark meetings search cache as stale without refetching immediately (needed for filtering)
			queryClient.invalidateQueries({
				queryKey: ["get-meetings-search"],
				refetchType: "none",
			});
		},
	});
};

export const useUpdateMeetingTranscript = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ meetingId, content }: { meetingId: string; content: string }) =>
			await updateMeetingTranscript(meetingId, content),
		onSuccess: (_, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-transcript", meetingId] });
		},
	});
};

/**
 * Manually pulls this meeting's transcript from its video conferencing provider. Only the meeting
 * creator can (`meetings:retrieveTranscript`). Invalidates the transcript so the editor shows the new
 * content, and the meeting details so the ingest status the banner reads reflects the attempt.
 */
export const useRetrieveMeetingTranscript = (): UseMutationResult<
	RetrieveMeetingTranscriptResponseJson | undefined,
	Error,
	{ meetingId: string }
> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ meetingId }: { meetingId: string }) => await retrieveMeetingTranscript(meetingId),
		// Awaited so the trigger stays disabled until the refetches land — otherwise a second press answers
		// "skipped" for a meeting that just received its transcript.
		onSuccess: async (_, { meetingId }) => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["get-meeting-transcript", meetingId] }),
				queryClient.invalidateQueries({ queryKey: ["get-meeting-details", meetingId] }),
			]);
		},
	});
};

export const useDismissTranscriptBanner = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ meetingId, bannerKey }: { meetingId: string; bannerKey: TranscriptBannerKey }) =>
			await dismissTranscriptBanner(meetingId, bannerKey),
		// Optimistic, no rollback: hide the banner immediately by writing the dismissed key into the
		// meeting-details cache. If the request fails the banner stays dismissed; worst case it
		// reappears on the next load and the user dismisses it again.
		onMutate: ({ meetingId, bannerKey }) => {
			queryClient.setQueryData<GetMeetingDetailsResponseJson>(["get-meeting-details", meetingId], (prev) => {
				if (!prev) return prev;
				const dismissed = prev.meeting.dismissedTranscriptBanners ?? [];
				if (dismissed.includes(bannerKey)) return prev;
				return {
					...prev,
					meeting: { ...prev.meeting, dismissedTranscriptBanners: [...dismissed, bannerKey] },
				};
			});
		},
	});
};

export const useUpcomingMeetings = (
	params: GetUpcomingMeetingsParams,
	options?: PYUseQueryOptions<GetUpcomingMeetingsResponseFrontend>
) => {
	const query = useQuery<GetUpcomingMeetingsResponseFrontend>({
		...upcomingMeetingsQuery(params),
		...options,
	});

	return {
		data: query?.data || [],
		isLoading: query?.isLoading,
	};
};
